import type {
  GuidedAnswer,
  GuidedModeCondition,
  GuidedModeDefinition,
  GuidedModeStep,
  GuidedModeVisibilityRule,
} from '../types/guided-mode.types'
import { questionForStep, assessmentQuestionSteps } from './assessment-definition'
import { evaluateCondition, GuidedModeAnswers } from './step-engine'

/*  Visibility is a tri-state question, not a yes/no one.

    `isStepVisible` answers "can this step be shown right now", which is the right question
    for rendering but the wrong one for the assess() payload. It reports false for a question
    whose gating answer has not been given yet, and it reports that as certainly hidden. Marking
    such a question skipped would claim the user passed it when in fact they have not reached
    it, and the assessment state would then read a still-open condition as finished.

    So the walk below tracks, per concept, whether the skip logic *definitively* hid it:
    true once the gate that closes it has been answered away, and still unknown while the gate
    is unanswered. Only the first of those means "the user will never be asked this". */
type VisibleState = true | false | undefined

interface HiddenConceptOptions {
  /*  Answers cleared by the caller rather than by the user. A cleared gate counts as closed,
      which is what stops a follow-up from reappearing after its parent was reset. */
  removed?: ReadonlySet<string>
  /*  Recorded symptoms that stay in the bundle while their question is hidden. A recorded
      symptom the skip logic would have hidden is still an answer the user gave. */
  keptSymptomConceptIds?: readonly string[]
}

/*  A gate that has been answered `no` where the condition wants something else. This is the
    one case where the gate answer alone settles visibility, so it resolves an "unknown" gate
    to a definite false without waiting for every sibling condition. */
function negativeGate(rule: GuidedModeCondition | GuidedModeVisibilityRule, values: GuidedModeAnswers): boolean {
  if ('all' in rule) return rule.all.some(child => negativeGate(child, values))
  if ('any' in rule) return rule.any.some(child => negativeGate(child, values))
  return values[rule.conceptId] === 'no' && !evaluateCondition(rule, values)
}

/*  The gate's verdict, or undefined while the answer it needs has not been given. */
function knownGate(
  rule: GuidedModeCondition | GuidedModeVisibilityRule,
  values: GuidedModeAnswers,
  removed: ReadonlySet<string>,
): VisibleState {
  if ('all' in rule) {
    const states = rule.all.map(child => knownGate(child, values, removed))
    if (states.includes(false)) return false
    return states.every(state => state === true) ? true : undefined
  }
  if ('any' in rule) {
    const states = rule.any.map(child => knownGate(child, values, removed))
    if (states.includes(true)) return true
    return states.every(state => state === false) ? false : undefined
  }
  if (values[rule.conceptId] === undefined) return removed.has(rule.conceptId) ? false : undefined
  return evaluateCondition(rule, values)
}

/*  The concepts a step renders. The step points at a question and the question owns the
    concepts: its own, plus the outputs of an aggregate multiselect (a symptom feeding its own
    follow-ups), each of which the questionnaire tracks separately. */
function conceptIdsForStep(
  definition: GuidedModeDefinition,
  step: GuidedModeStep,
): string[] {
  if (step.kind === 'group') return []
  const question = questionForStep(definition, step)
  if (!question) return []
  return [question.conceptId, ...(question.outputs || []).map(output => output.conceptId)]
    .filter((id): id is string => Boolean(id))
}

/*  Walk the assessment depth first, carrying two things down the tree: whether an ancestor has
    already closed the branch (which settles every descendant as hidden), and whether that
    closure came from an answered `no` (which is the only closure a recorded symptom may not
    override). */
function hiddenConceptIds(
  definition: GuidedModeDefinition | null | undefined,
  values: GuidedModeAnswers = {},
  { removed = new Set<string>(), keptSymptomConceptIds = [] }: HiddenConceptOptions = {},
): Set<string> {
  if (!definition) return new Set<string>()

  const permitted = new Map<string, boolean>()

  const visit = (step: GuidedModeStep, ancestorVisible: VisibleState, parentNo = false): void => {
    let ownVisible: VisibleState = step.visibleWhen ? knownGate(step.visibleWhen, values, removed) : true

    // An unanswered gate that already holds a contradicted `no` is settled: the condition
    // cannot come true, so the question is off the screen rather than merely pending.
    if (ownVisible === undefined && step.visibleWhen && negativeGate(step.visibleWhen, values)
      && !evaluateCondition(step.visibleWhen, values)) ownVisible = false

    const visible: VisibleState = ancestorVisible === false || ownVisible === false ? false
      : ancestorVisible === true && ownVisible === true ? true : undefined

    const closedByNo = parentNo
      || (ownVisible !== true && Boolean(step.visibleWhen && negativeGate(step.visibleWhen, values)))

    if (step.kind === 'group') {
      for (const child of step.steps || []) visit(child, visible, closedByNo)
      return
    }

    for (const id of conceptIdsForStep(definition, step)) {
      // A question kept by the caller survives a branch it has not been offered for, but not a
      // branch the user closed with `no`: that answer means the follow-up is not applicable.
      const preserved = keptSymptomConceptIds.includes(id) && !closedByNo
      permitted.set(id, Boolean(permitted.get(id)) || visible !== false || preserved)
    }
  }

  for (const step of assessmentQuestionSteps(definition)) visit(step, true)

  return new Set(Array.from(permitted).filter(([, allowed]) => !allowed).map(([id]) => id))
}

/*  The concepts the assess() payload has to mark with `isSkip`: the ones the skip logic
    definitively hid. A question whose gating answer has not been given yet is left unmarked,
    because it is still ahead of the user rather than behind them. */
export function skippedConceptIds(
  definition: GuidedModeDefinition | null | undefined,
  answers: GuidedModeAnswers,
  keptSymptomConceptIds: readonly string[] = [],
): Set<string> {
  return hiddenConceptIds(definition, answers, { keptSymptomConceptIds })
}

export { hiddenConceptIds }